import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import {
  Shield, CheckCircle, XCircle, RefreshCw, Users, BarChart2,
  ClipboardList, Building2, LogOut, Clock, ChevronRight,
  DollarSign, TrendingUp, Edit2, X, Check, Plus, Eye, EyeOff,
  Mail, Phone, ArrowRight, ArrowLeft, Sparkles, Copy, RotateCcw
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_COLORS = {
  approved: "bg-green-100 text-green-700 border-green-200",
  pending:  "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const PLAN_COLORS = {
  starter:      "bg-blue-50 text-blue-700 border-blue-200",
  professional: "bg-purple-50 text-purple-700 border-purple-200",
  enterprise:   "bg-navy/10 text-navy border-navy/20",
  custom:       "bg-slate-100 text-slate-600 border-slate-200",
  "":           "bg-slate-100 text-slate-400 border-slate-200",
};

const PLAN_OPTIONS = [
  { key: "starter",      label: "Starter",      fee: 500,  included: 10,  overage: 65 },
  { key: "professional", label: "Professional", fee: 1500, included: 30,  overage: 55 },
  { key: "enterprise",   label: "Enterprise",   fee: 3500, included: 100, overage: 45 },
  { key: "custom",       label: "Custom",       fee: 0,    included: 0,   overage: 0  },
];

const INSPECTION_TYPE_OPTIONS = [
  { key: "commercial_property", label: "Commercial Property" },
  { key: "workers_comp",        label: "Workers' Compensation" },
  { key: "general_liability",   label: "General Liability" },
  { key: "habitational",        label: "Habitational" },
  { key: "commercial_auto",     label: "Commercial Auto" },
];

function genPassword() {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

/* ── Provision Wizard Modal ─────────────────────────── */
function ProvisionWizard({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [copied, setCopied] = useState("");

  const [form, setForm] = useState({
    carrier_name: "",
    contact_email: "",
    contact_phone: "",
    plan: "professional",
    admin_name: "",
    admin_email: "",
    admin_password: genPassword(),
    send_welcome_email: true,
    inspection_types: ["commercial_property", "workers_comp", "general_liability", "habitational", "commercial_auto"],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleType = (key) => {
    set("inspection_types", form.inspection_types.includes(key)
      ? form.inspection_types.filter(t => t !== key)
      : [...form.inspection_types, key]
    );
  };

  const handleSubmit = async () => {
    setError("");
    setSaving(true);
    try {
      const res = await api.post("/platform/carriers/provision", form);
      setResult(res.data);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || "Provisioning failed. Please try again.");
    }
    setSaving(false);
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="provision-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Modal header */}
        <div className="bg-navy px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-white text-lg">
              {result ? "Carrier Provisioned" : "New Carrier Setup"}
            </h2>
            {!result && (
              <p className="text-white/60 text-xs mt-0.5">Step {step} of 2</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step progress */}
        {!result && (
          <div className="flex gap-1 px-6 pt-4">
            {[1, 2].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? "bg-navy" : "bg-slate-200"}`} />
            ))}
          </div>
        )}

        <div className="p-6">
          {/* ── Success state ── */}
          {result ? (
            <div className="space-y-5" data-testid="provision-success">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 text-sm">{result.carrier_name} is live</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {result.welcome_email_sent
                      ? `Welcome email sent to ${result.admin_email}`
                      : "Carrier created. Share credentials manually."}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Login Credentials</p>
                {[
                  { label: "Email",    value: result.admin_email,    key: "email" },
                  { label: "Password", value: form.admin_password, key: "pass" },
                ].map(row => (
                  <div key={row.key} className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-400">{row.label}</p>
                      <p className="text-sm font-mono font-semibold text-slate-800">{row.value}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(row.value, row.key)}
                      className="flex items-center gap-1 text-xs text-navy hover:text-navy/70 transition-colors px-2 py-1 rounded-md hover:bg-navy/5"
                    >
                      {copied === row.key ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied === row.key ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>

              <button onClick={onClose}
                className="w-full h-10 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 transition-colors">
                Done
              </button>
            </div>
          ) : step === 1 ? (
            /* ── Step 1: Carrier Info ── */
            <div className="space-y-4" data-testid="provision-step-1">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Insurance Company Name *</label>
                <input data-testid="provision-carrier-name"
                  type="text" value={form.carrier_name} onChange={e => set("carrier_name", e.target.value)}
                  placeholder="Acme Mutual Insurance"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Contact Email</label>
                  <input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)}
                    placeholder="contact@acme.com"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Phone</label>
                  <input type="tel" value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)}
                    placeholder="(555) 000-0000"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Plan</label>
                <div className="grid grid-cols-2 gap-2">
                  {PLAN_OPTIONS.map(p => (
                    <button key={p.key} type="button" onClick={() => set("plan", p.key)}
                      data-testid={`plan-option-${p.key}`}
                      className={`p-3 rounded-xl border text-left transition-all ${form.plan === p.key ? "border-navy bg-navy/5 ring-2 ring-navy/20" : "border-slate-200 hover:border-slate-300"}`}>
                      <p className="text-sm font-semibold text-slate-800">{p.label}</p>
                      {p.fee > 0
                        ? <p className="text-xs text-slate-400">${p.fee.toLocaleString()}/mo · {p.included} surveys</p>
                        : <p className="text-xs text-slate-400">Custom pricing</p>
                      }
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Inspection Types</label>
                <div className="grid grid-cols-2 gap-2">
                  {INSPECTION_TYPE_OPTIONS.map(t => (
                    <label key={t.key} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                      form.inspection_types.includes(t.key) ? "border-navy/30 bg-navy/5" : "border-slate-200"
                    }`}>
                      <input type="checkbox" checked={form.inspection_types.includes(t.key)}
                        onChange={() => toggleType(t.key)} className="accent-navy" />
                      <span className="text-xs text-slate-700 font-medium">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={() => { if (!form.carrier_name.trim()) { setError("Company name is required"); return; } setError(""); setStep(2); }}
                className="w-full h-10 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 transition-colors flex items-center justify-center gap-2"
                data-testid="provision-next-btn">
                Next: Admin User <ArrowRight className="w-4 h-4" />
              </button>
              {error && <p className="text-xs text-red-600 text-center">{error}</p>}
            </div>
          ) : (
            /* ── Step 2: Admin User ── */
            <div className="space-y-4" data-testid="provision-step-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Admin Name</label>
                  <input data-testid="provision-admin-name"
                    type="text" value={form.admin_name} onChange={e => set("admin_name", e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Admin Email *</label>
                  <input data-testid="provision-admin-email"
                    type="email" value={form.admin_email} onChange={e => set("admin_email", e.target.value)}
                    placeholder="jane@acme.com"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Temporary Password *</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input data-testid="provision-admin-password"
                      type={showPass ? "text" : "password"} value={form.admin_password}
                      onChange={e => set("admin_password", e.target.value)}
                      className="w-full h-10 px-3 pr-10 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors" />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button type="button" onClick={() => set("admin_password", genPassword())}
                    title="Generate new password"
                    className="w-10 h-10 flex items-center justify-center border border-slate-200 rounded-lg text-slate-400 hover:text-navy hover:border-navy transition-colors">
                    <Sparkles className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">The admin will be prompted to change this on first login.</p>
              </div>

              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 transition-colors">
                <input type="checkbox" checked={form.send_welcome_email}
                  onChange={e => set("send_welcome_email", e.target.checked)} className="accent-navy" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Send welcome email</p>
                  <p className="text-xs text-slate-400">Deliver login credentials to {form.admin_email || "the admin"}</p>
                </div>
              </label>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">{error}</div>
              )}

              <div className="flex gap-3">
                <button onClick={() => { setError(""); setStep(1); }}
                  className="flex items-center gap-1.5 px-4 h-10 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={handleSubmit} disabled={saving || !form.admin_email}
                  data-testid="provision-submit-btn"
                  className="flex-1 h-10 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Creating...</>
                    : <><CheckCircle className="w-4 h-4" /> Create Carrier</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function PlatformAdmin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [billing, setBilling] = useState(null);
  const [tab, setTab] = useState("carriers");
  const [loading, setLoading] = useState(true);
  const [planModal, setPlanModal] = useState(null); // { id, name, plan, ... }
  const [showProvision, setShowProvision] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleDemoReset = async () => {
    if (!window.confirm("Reset the demo carrier to its polished state?\n\nThis restores the 6 demo inspections, field-visit status, and recommendation history — wiping any changes made during demos.")) return;
    setResetting(true);
    try {
      const res = await api.post("/platform/demo/reset");
      toast.success(`Demo reset complete — ${res.data.inspections} inspections, ${res.data.recommendations} recommendations restored`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo reset failed");
    }
    setResetting(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, carriersRes, logsRes] = await Promise.all([
        api.get("/platform/stats"),
        api.get("/platform/carriers"),
        api.get("/platform/audit-logs"),
      ]);
      setStats(statsRes.data);
      setCarriers(carriersRes.data);
      setAuditLogs(logsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadBilling = async () => {
    try {
      const res = await api.get("/platform/billing-summary");
      setBilling(res.data);
    } catch (e) {
      console.error(e);
      setBilling({ error: true, month: "—", carriers: [], total_mrr: 0, total_overage: 0, grand_total: 0 });
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "billing") loadBilling(); }, [tab]);

  const handleApprove = async (id) => {
    await api.patch(`/platform/carriers/${id}/approve`);
    setCarriers(prev => prev.map(c => c.id === id ? { ...c, approval_status: "approved", active: true } : c));
    setStats(prev => prev ? { ...prev, pending_approval: Math.max(0, prev.pending_approval - 1) } : prev);
  };

  const handleReject = async (id) => {
    await api.patch(`/platform/carriers/${id}/reject`);
    setCarriers(prev => prev.map(c => c.id === id ? { ...c, approval_status: "rejected", active: false } : c));
    setStats(prev => prev ? { ...prev, pending_approval: Math.max(0, prev.pending_approval - 1) } : prev);
  };

  const pendingCarriers = carriers.filter(c => c.approval_status === "pending");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-heading font-bold text-white">RiskSurvey.ai</span>
            <span className="ml-2 text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full">Platform Admin</span>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Carriers",   value: stats.total_carriers,   icon: Building2,    color: "text-navy",        bg: "bg-blue-50" },
              { label: "Pending Approval", value: stats.pending_approval, icon: Clock,        color: "text-amber-600",  bg: "bg-amber-50" },
              { label: "Total Surveys",    value: stats.total_surveys,    icon: ClipboardList, color: "text-green-600", bg: "bg-green-50" },
              { label: "Total Users",      value: stats.total_users,      icon: Users,        color: "text-purple-600", bg: "bg-purple-50" },
            ].map(card => (
              <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${card.label.toLowerCase().replace(/ /g,'-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
                  <div className={`w-8 h-8 ${card.bg} rounded-lg flex items-center justify-center`}>
                    <card.icon className={`w-4 h-4 ${card.color}`} />
                  </div>
                </div>
                <div className="font-heading text-3xl font-bold text-slate-800">{card.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Pending alert */}
        {pendingCarriers.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between" data-testid="pending-alert">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-amber-800 text-sm font-medium">{pendingCarriers.length} carrier{pendingCarriers.length > 1 ? "s" : ""} waiting for approval</span>
            </div>
            <button onClick={() => setTab("carriers")} className="text-amber-700 text-xs font-semibold hover:underline">Review →</button>
          </div>
        )}

        {/* Tabs + New Carrier button */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { key: "carriers", label: "Carriers" },
              { key: "billing",  label: "Billing & Usage" },
              { key: "logs",     label: "Activity Log" },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`platform-tab-${t.key}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="demo-reset-btn"
              disabled={resetting}
              onClick={handleDemoReset}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-semibold hover:border-amber-400 hover:text-amber-700 transition-colors shadow-sm disabled:opacity-60">
              {resetting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {resetting ? "Resetting…" : "Reset Demo Data"}
            </button>
            <button
              data-testid="new-carrier-btn"
              onClick={() => setShowProvision(true)}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 transition-colors shadow-sm">
              <Plus className="w-4 h-4" /> New Carrier
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-navy" /></div>
        ) : tab === "carriers" ? (
          <div className="space-y-3">
            {carriers.map(c => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-testid={`carrier-row-${c.id}`}>
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[c.approval_status] || STATUS_COLORS.pending}`}>
                      {c.approval_status}
                    </span>
                    {c.plan_name && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${PLAN_COLORS[c.plan] || PLAN_COLORS[""]}`}>
                        {c.plan_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{c.contact_email} · {c.user_count} users · {c.survey_count} surveys</p>
                  {c.created_at && <p className="text-xs text-slate-300 mt-0.5">Registered {new Date(c.created_at).toLocaleDateString()}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPlanModal(c)} data-testid={`assign-plan-${c.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-600 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-100 transition-colors">
                    <Edit2 className="w-3 h-3" /> {c.plan_name || "Assign Plan"}
                  </button>
                  {c.approval_status === "pending" && (
                    <>
                      <button onClick={() => handleApprove(c.id)} data-testid={`approve-${c.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors">
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => handleReject(c.id)} data-testid={`reject-${c.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors">
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {carriers.length === 0 && (
              <div className="py-12 text-center text-slate-400">No carriers registered yet</div>
            )}
          </div>

        ) : tab === "billing" ? (
          <BillingTab billing={billing} onRefresh={loadBilling} />

        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead><tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Time</th>
              </tr></thead>
              <tbody>
                {auditLogs.map((log, i) => (
                  <tr key={log.id} className={`border-b border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                    <td className="px-4 py-3 font-medium text-slate-700">{log.action}</td>
                    <td className="px-4 py-3 text-slate-500">{log.user_email || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{log.carrier_id || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {auditLogs.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No activity yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Plan Assignment Modal */}
      {planModal && (
        <PlanModal carrier={planModal} onClose={() => setPlanModal(null)}
          onSaved={(updated) => {
            setCarriers(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
            setPlanModal(null);
          }}
        />
      )}

      {/* Provision Wizard */}
      {showProvision && (
        <ProvisionWizard
          onClose={() => setShowProvision(false)}
          onSuccess={() => { load(); }}
        />
      )}
    </div>
  );
}

function BillingTab({ billing, onRefresh }) {
  if (!billing) return (
    <div className="py-12 flex flex-col items-center gap-3" data-testid="billing-loading">
      <RefreshCw className="w-6 h-6 animate-spin text-navy" />
      <button onClick={onRefresh} className="text-xs text-slate-400 hover:text-navy transition-colors">
        Taking too long? Click to retry
      </button>
    </div>
  );

  const fmt = (n) => `$${n.toLocaleString()}`;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Month header + MRR summary */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-lg text-navy">{billing.month} — Billing Summary</h2>
          <p className="text-xs text-slate-500 mt-0.5">Surveys billed on completion only</p>
        </div>
        <button onClick={onRefresh} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy transition-colors border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Base MRR",      value: fmt(billing.total_mrr),     icon: DollarSign,  color: "text-navy",       bg: "bg-blue-50" },
          { label: "Overage",       value: fmt(billing.total_overage),  icon: TrendingUp,  color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Total to Bill", value: fmt(billing.grand_total),    icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
              <div className={`w-7 h-7 ${card.bg} rounded-lg flex items-center justify-center`}>
                <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
              </div>
            </div>
            <div className="font-heading text-2xl font-bold text-slate-800">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Per-carrier table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="billing-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["Carrier", "Plan", "Base Fee", "Included", "Completed", "Overage", "Overage $", "Total Invoice"].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {billing.carriers.map((c, i) => {
              const over = c.overage_count > 0;
              return (
                <tr key={c.id} className={`border-b border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}
                  data-testid={`billing-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{c.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[c.approval_status] || ""}`}>{c.approval_status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PLAN_COLORS[c.plan] || PLAN_COLORS[""]}`}>
                      {c.plan_name || "No Plan"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(c.monthly_fee)}</td>
                  <td className="px-4 py-3 text-slate-600">{c.included_surveys}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{c.surveys_completed}</td>
                  <td className="px-4 py-3">
                    <span className={over ? "font-semibold text-amber-600" : "text-slate-400"}>{c.overage_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={over ? "font-semibold text-amber-600" : "text-slate-400"}>{fmt(c.overage_amount)}</span>
                  </td>
                  <td className="px-4 py-3 font-bold text-navy">{fmt(c.total_invoice)}</td>
                </tr>
              );
            })}
            {billing.carriers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No carriers yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <InvoicesPanel carriers={billing.carriers || []} />
    </div>
  );
}

function InvoicesPanel({ carriers }) {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({
    carrier_id: "", period: new Date().toISOString().slice(0, 7),
    po_number: "", ap_email: "", platform_fee: "", rate_ai: "", rate_form: "",
  });

  const load = () => api.get("/platform/invoices").then(r => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.carrier_id) return;
    setBusy("gen");
    try {
      await api.post("/platform/invoices/generate", {
        ...form,
        platform_fee: parseFloat(form.platform_fee) || 0,
        rate_ai: parseFloat(form.rate_ai) || 0,
        rate_form: parseFloat(form.rate_form) || 0,
      });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Generate failed"); }
    setBusy("");
  };

  const act = async (id, action) => {
    setBusy(id);
    try {
      if (action === "send") await api.post(`/platform/invoices/${id}/send`);
      else if (action === "paid") await api.patch(`/platform/invoices/${id}/status`, { status: "paid" });
      else if (action === "pdf") {
        const res = await api.get(`/platform/invoices/${id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = "invoice.pdf"; a.click();
        URL.revokeObjectURL(url);
      }
      load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  const INV_COLORS = { draft: "bg-slate-100 text-slate-600", sent: "bg-blue-50 text-blue-700", paid: "bg-green-50 text-green-700", void: "bg-red-50 text-red-600" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="invoices-panel">
      <h3 className="font-heading font-semibold text-slate-700 mb-1">Invoices (PO / Net-30)</h3>
      <p className="text-xs text-slate-400 mb-4">Generate a branded PDF invoice from monthly usage, email it to the carrier's AP contact, and track payment.</p>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
        <select data-testid="inv-carrier-select" value={form.carrier_id} onChange={e => setForm(f => ({ ...f, carrier_id: e.target.value }))}
          className="text-xs border border-slate-200 rounded-md px-2 py-2 bg-white md:col-span-2">
          <option value="">Select carrier…</option>
          {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input data-testid="inv-period-input" type="month" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))}
          className="text-xs border border-slate-200 rounded-md px-2 py-2" />
        <input data-testid="inv-po-input" placeholder="PO #" value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))}
          className="text-xs border border-slate-200 rounded-md px-2 py-2" />
        <input data-testid="inv-ap-input" placeholder="AP email" value={form.ap_email} onChange={e => setForm(f => ({ ...f, ap_email: e.target.value }))}
          className="text-xs border border-slate-200 rounded-md px-2 py-2" />
        <input data-testid="inv-fee-input" placeholder="Platform fee $" value={form.platform_fee} onChange={e => setForm(f => ({ ...f, platform_fee: e.target.value }))}
          className="text-xs border border-slate-200 rounded-md px-2 py-2" />
        <div className="flex gap-1">
          <input data-testid="inv-rateai-input" placeholder="AI $/ea" value={form.rate_ai} onChange={e => setForm(f => ({ ...f, rate_ai: e.target.value }))}
            className="text-xs border border-slate-200 rounded-md px-2 py-2 w-1/2" />
          <input data-testid="inv-rateform-input" placeholder="Form $/ea" value={form.rate_form} onChange={e => setForm(f => ({ ...f, rate_form: e.target.value }))}
            className="text-xs border border-slate-200 rounded-md px-2 py-2 w-1/2" />
        </div>
      </div>
      <button data-testid="generate-invoice-btn" disabled={busy === "gen" || !form.carrier_id} onClick={generate}
        className="px-4 py-2 bg-navy text-white rounded-lg text-xs font-semibold hover:bg-navy/90 transition-colors disabled:opacity-50 mb-4">
        {busy === "gen" ? "Generating…" : "Generate Invoice"}
      </button>
      {invoices.length > 0 && (
        <table className="w-full text-xs" data-testid="invoices-table">
          <thead><tr className="text-left text-slate-400 uppercase tracking-wider border-b border-slate-100">
            <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Carrier</th><th className="py-2 pr-3">Period</th>
            <th className="py-2 pr-3">PO</th><th className="py-2 pr-3">Total</th><th className="py-2 pr-3">Status</th><th></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {invoices.map(inv => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <td className="py-2 pr-3 font-mono">{inv.number}</td>
                <td className="py-2 pr-3">{inv.carrier_name}</td>
                <td className="py-2 pr-3">{inv.period}</td>
                <td className="py-2 pr-3">{inv.po_number || "—"}</td>
                <td className="py-2 pr-3 font-semibold">${inv.total.toLocaleString()}</td>
                <td className="py-2 pr-3"><span className={`px-1.5 py-0.5 rounded-full font-semibold ${INV_COLORS[inv.status]}`}>{inv.status}</span></td>
                <td className="py-2 text-right space-x-1">
                  <button data-testid={`inv-pdf-btn-${inv.id}`} onClick={() => act(inv.id, "pdf")} className="text-navy hover:underline">PDF</button>
                  {inv.status !== "paid" && <button data-testid={`inv-send-btn-${inv.id}`} disabled={busy === inv.id} onClick={() => act(inv.id, "send")} className="text-blue-600 hover:underline">Send</button>}
                  {inv.status === "sent" && <button data-testid={`inv-paid-btn-${inv.id}`} disabled={busy === inv.id} onClick={() => act(inv.id, "paid")} className="text-green-600 hover:underline">Mark Paid</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PlanModal({ carrier, onClose, onSaved }) {
  const [selected, setSelected] = useState(carrier.plan || "starter");
  const [custom, setCustom] = useState({
    monthly_fee:      carrier.plan_monthly_fee || 0,
    included_surveys: carrier.plan_included_surveys || 0,
    overage_rate:     carrier.plan_overage_rate || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { plan: selected, ...custom };
      await api.put(`/platform/carriers/${carrier.id}/plan`, payload);
      const plan = PLAN_OPTIONS.find(p => p.key === selected);
      onSaved({
        id: carrier.id,
        plan: selected,
        plan_name: plan?.label || "Custom",
        plan_monthly_fee: selected === "custom" ? custom.monthly_fee : plan?.fee,
        plan_included_surveys: selected === "custom" ? custom.included_surveys : plan?.included,
        plan_overage_rate: selected === "custom" ? custom.overage_rate : plan?.overage,
      });
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="plan-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-heading font-bold text-navy text-lg">Assign Plan</h3>
            <p className="text-sm text-slate-500">{carrier.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="space-y-2 mb-5">
          {PLAN_OPTIONS.map(p => (
            <button key={p.key} onClick={() => setSelected(p.key)} data-testid={`plan-option-${p.key}`}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all ${selected === p.key ? "border-navy bg-navy/5 text-navy" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected === p.key ? "border-navy" : "border-slate-300"}`}>
                  {selected === p.key && <div className="w-2 h-2 bg-navy rounded-full" />}
                </div>
                <span className="font-semibold">{p.label}</span>
              </div>
              {p.key !== "custom" && (
                <span className="text-xs text-slate-500">${p.fee}/mo · {p.included} surveys · ${p.overage}/extra</span>
              )}
            </button>
          ))}
        </div>

        {selected === "custom" && (
          <div className="grid grid-cols-3 gap-3 mb-5 p-4 bg-slate-50 rounded-xl border border-slate-200">
            {[
              { key: "monthly_fee",      label: "Monthly Fee ($)" },
              { key: "included_surveys", label: "Included" },
              { key: "overage_rate",     label: "Overage Rate ($)" },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs font-medium text-slate-500 block mb-1">{f.label}</label>
                <input type="number" value={custom[f.key]}
                  onChange={e => setCustom(prev => ({ ...prev, [f.key]: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
                  data-testid={`custom-${f.key}`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-plan-btn"
            className="flex-1 px-4 py-2 text-sm font-semibold bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

